import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import * as clientsApi from '../../api/clients';
import { getClientContacts } from '../../api/contacts';
import { Contact } from '../../types/contact.types';
import { Client } from '../../types/client';
import { useSnackbar } from 'notistack';

interface IssueLOAButtonProps {
  client: Client;
  onLOAIssued: () => void;
}

const IssueLOAButton: React.FC<IssueLOAButtonProps> = ({ client, onLOAIssued }) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const canIssueLOA = client.properties && client.properties.length > 0;

  // Audit 07 R-5: load the client's contacts when the dialog opens so the
  // issuer deliberately picks the addressee (default = active PRIMARY).
  useEffect(() => {
    if (!showConfirmDialog) return;
    setSelectedContactId('');
    getClientContacts(client._id).then(setContacts).catch(() => setContacts([]));
  }, [showConfirmDialog, client._id]);

  const handleIssueLOA = async () => {
    try {
      setLoading(true);

      // Generate and send LOA
      await clientsApi.issueLOA(client._id, selectedContactId || undefined);

      enqueueSnackbar('Letter of Authority has been issued successfully', { variant: 'success' });
      onLOAIssued();
      setShowConfirmDialog(false);
    } catch (error: any) {

      enqueueSnackbar(error.response?.data?.message || 'Failed to issue Letter of Authority', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Audit 14/F-167: resend replays the existing issued artefact — the same
  // token, PDF and version. Use this for a failed/lost email; issuing again
  // creates a NEW version and supersedes the pending one.
  const handleResendLOA = async () => {
    if (!client.currentLoa) return;
    try {
      setLoading(true);
      await clientsApi.resendLOA(client.currentLoa);
      enqueueSnackbar('Letter of Authority resent', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to resend Letter of Authority', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loaPending = client.loaStatus === 'ISSUED' && !!client.currentLoa;

  if (!canIssueLOA) {
    return (
      <Button
        variant="contained"
        disabled
        title="Add at least one property to issue LOA"
      >
        Issue LOA
      </Button>
    );
  }

  const primary = contacts.find(c => c.roles.includes('PRIMARY'));

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setShowConfirmDialog(true)}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Issue LOA'}
      </Button>
      {loaPending && (
        <Button
          variant="outlined"
          onClick={handleResendLOA}
          disabled={loading}
          sx={{ ml: 1 }}
          title="Resend the existing LOA link — same document, same version"
        >
          Resend LOA
        </Button>
      )}

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 25, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Issue Letter of Authority</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            This will generate a Letter of Authority for {client.companyName} and send it via email.
            The client will be able to review and accept/decline the LOA through a secure link.
            {loaPending && (
              <>
                {' '}A pending LOA already exists — issuing now creates a new version and supersedes it.
                Use “Resend LOA” to re-deliver the existing link instead.
              </>
            )}
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="loa-contact-label" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Send to contact
            </InputLabel>
            <Select
              labelId="loa-contact-label"
              value={selectedContactId}
              label="Send to contact"
              onChange={(e) => setSelectedContactId(e.target.value)}
              sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
            >
              <MenuItem value="">
                <em>
                  {primary
                    ? `Default — ${primary.name}${primary.email ? ` (${primary.email})` : ''} [PRIMARY]`
                    : 'Default — primary contact / client email'}
                </em>
              </MenuItem>
              {contacts.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}{c.email ? ` — ${c.email}` : ''}{c.roles?.length ? ` [${c.roles.join(', ')}]` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleIssueLOA}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            Issue LOA
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IssueLOAButton;
